"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import axios from "axios"
import { Box, Select, Text, Alert, AlertIcon, AlertDescription, useToast } from "@chakra-ui/react"
import { FaUserTie, FaStore, FaFileExcel } from "react-icons/fa"

import PageToolbar from "../../components/shared/PageToolbar"
import Section, { Panel } from "../../components/shared/Section"
import ExportMenu from "../../components/shared/ExportMenu"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"
import { neutral, brand, type } from "../../theme/tokens"

import RepKpis from "./components/RepKpis"
import RepSummary from "./components/RepSummary"
import RepTracker from "./components/RepTracker"
import RepChart from "./components/RepChart"

const API = process.env.REACT_APP_API_URL
const ALL = "ALL"
const REP_KEY = "spearbi_rep_selected"
const RET_KEY = "spearbi_rep_retailer_v2" // v2 (2026-10-17, Helena): opens on All retailers; the old key kept everyone on Dis-Chem
// Rep Sales per retailer (2026-10-16): Dis-Chem, Checkers, MediRite, Clicks — each rep's stores at that retailer.
const RET_LABEL = { ALL: "All retailers", DIS: "Dis-Chem", CHK: "Checkers", MED: "MediRite", CLI: "Clicks" }

/**
 * Rep page (docs/michael/blue-mango-rep-report/TASK-rep-report.md, 2026-09-21).
 *
 * Organisation-scoped: every number combines all of the organisation's suppliers (the
 * "Combined" switcher in the rail) and the Dischem channel only — so no supplier or
 * retailer rides in the requests, only the page's own filters (rep, supplier) and, for a
 * master admin, the org toggle (org_code). The backend refuses an organisation without
 * rep→store assignments (403 REP_NOT_SET_UP), which this page renders as a notice.
 *
 * Numbers are sales AT COST from dis_sales.cost — the workbook's data!H — never retail.
 */
// Combined page (RepHub, 2026-10-12): `routing` = the Rep Routing summary, joined into the All reps table per rep;
// `chartTarget` = a node at the foot of that page — the Sales at cost by rep chart renders there, not beside the table.
// `storesTarget` (2026-10-16): the hub's Stores tab — the every-store list renders there instead of as a block.
// onStoreSales / hideStores (2026-10-16, Helena "have it all in one"): the hub shows each store's sales on the visit
// lines instead of a Stores tab — Rep hands up every store's figures (rep = all) for the picked supplier + retailer.
const Rep = ({ routing = null, onRoute, chartTarget = null, storesTarget = null, onStoreSales = null, hideStores = false }) => {
  const { selectedOrg, isMaster } = useSupplierRetailer()
  const toast = useToast()

  const [options, setOptions] = useState(null)
  const [optionsError, setOptionsError] = useState(null) // { code, message }
  const [supplier, setSupplier] = useState(ALL)
  const [retailer, setRetailer] = useState(() => { try { const v = localStorage.getItem(RET_KEY); return RET_LABEL[v] ? v : "ALL" } catch (e) { return "ALL" } })
  useEffect(() => { try { localStorage.setItem(RET_KEY, retailer) } catch (e) { /* private mode */ } }, [retailer])
  const [rep, setRep] = useState(() => { try { return localStorage.getItem(REP_KEY) || "" } catch (e) { return "" } })
  // a rep line in the All reps table picks that rep; the same line again = all reps
  const pickRep = (r) => setRep((p) => (p === r ? ALL : r))
  // The chart always opens on the current month (Helena 2026-10-13) — a switch to the previous month is no longer remembered.
  const [pair, setPair] = useState("cur")
  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [tracker, setTracker] = useState(null)
  const [trackerLoading, setTrackerLoading] = useState(true)
  const [exporting, setExporting] = useState(false)

  const orgArg = isMaster ? { org_code: selectedOrg } : {}
  const body = useCallback((extra = {}) => ({ userid: localStorage.getItem("userid"), ...orgArg, ...extra }), [selectedOrg, isMaster]) // eslint-disable-line react-hooks/exhaustive-deps

  // Options: reps, suppliers, period. Re-fetched when a master changes organisation.
  useEffect(() => {
    const controller = new AbortController()
    setOptions(null)
    setOptionsError(null)
    setSupplier(ALL)
    axios
      .post(`${API}/rep_report/options`, body({ retailer }), { signal: controller.signal })
      .then((res) => {
        const data = res.data?.data
        setOptions(data)
        // Keep the remembered rep when it still exists, else the combined view.
        const names = (data?.reps || []).map((r) => r.name)
        setRep((prev) => (prev === ALL || prev === "Unassigned" || names.includes(prev) ? prev : ALL))
      })
      .catch((err) => {
        if (axios.isCancel(err)) return
        const d = err.response?.data
        setOptionsError({ code: d?.code || (err.response?.status === 403 ? "FORBIDDEN" : "ERROR"), message: d?.message || "The Rep page could not be loaded." })
      })
    return () => controller.abort()
  }, [selectedOrg, isMaster, retailer]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { try { if (rep) localStorage.setItem(REP_KEY, rep) } catch (e) { /* private mode */ } }, [rep])

  // Summary (every rep) follows the supplier filter.
  useEffect(() => {
    if (!options) return undefined
    const controller = new AbortController()
    setSummaryLoading(true)
    axios
      .post(`${API}/rep_report/summary`, body({ supplier, retailer }), { signal: controller.signal })
      .then((res) => setSummary(res.data?.data || null))
      .catch((err) => { if (!axios.isCancel(err)) toast({ title: "Summary failed", description: err.response?.data?.message || err.message, status: "error", duration: 5000, isClosable: true }) })
      .finally(() => { if (!controller.signal.aborted) setSummaryLoading(false) })
    return () => controller.abort()
  }, [options, supplier]) // eslint-disable-line react-hooks/exhaustive-deps

  // Tracker (one rep's stores) follows the rep and the supplier filter.
  useEffect(() => {
    if (!options || !rep) return undefined
    const controller = new AbortController()
    setTrackerLoading(true)
    axios
      .post(`${API}/rep_report/tracker`, body({ rep, supplier, retailer }), { signal: controller.signal })
      .then((res) => setTracker(res.data?.data || null))
      .catch((err) => { if (!axios.isCancel(err)) toast({ title: "Tracker failed", description: err.response?.data?.message || err.message, status: "error", duration: 5000, isClosable: true }) })
      .finally(() => { if (!controller.signal.aborted) setTrackerLoading(false) })
    return () => controller.abort()
  }, [options, rep, supplier]) // eslint-disable-line react-hooks/exhaustive-deps

  // Every store's sales (all reps) for the hub's visit lines.
  useEffect(() => {
    if (!options || !onStoreSales) return undefined
    const controller = new AbortController()
    axios
      .post(`${API}/rep_report/tracker`, body({ rep: ALL, supplier, retailer }), { signal: controller.signal })
      .then((res) => onStoreSales({ ...(res.data?.data || {}), retailer }))
      .catch((err) => { if (!axios.isCancel(err)) onStoreSales(null) })
    return () => controller.abort()
  }, [options, supplier, retailer, onStoreSales]) // eslint-disable-line react-hooks/exhaustive-deps

  // Excel — same endpoint for one rep or every rep; window.fetch carries the bearer token
  // (tools/demoInterceptor.js), exactly as the other report downloads do.
  const download = useCallback(async (which) => {
    const repArg = which === ALL ? ALL : rep
    const safe = (s) => String(s).replace(/[^A-Za-z0-9 _.-]+/g, "_").trim()
    const fileName = `Rep Sales_${RET_LABEL[retailer]}_${safe(repArg === ALL ? "All reps" : repArg === "Unassigned" ? "Not assigned" : repArg)}_${safe(supplier)}.xlsx`
    try {
      setExporting(true)
      // (rep = ALL → Summary + All stores + one sheet per rep)
      const res = await fetch(`${API}/rep_report/tracker-xlsx`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body({ rep: repArg, supplier, retailer })),
      })
      const ct = res.headers.get("content-type") || ""
      if (!res.ok || !ct.includes("spreadsheetml")) {
        let message = `HTTP ${res.status}`
        try { message = (await res.json()).message || message } catch (e) { /* not json */ }
        throw new Error(message)
      }
      const blob = await res.blob()
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(link.href)
      toast({ title: "Downloaded", description: fileName, status: "success", duration: 4000, isClosable: true })
    } catch (err) {
      toast({ title: "Download failed", description: err.message, status: "error", duration: 6000, isClosable: true })
    } finally {
      setExporting(false)
    }
  }, [rep, supplier, retailer, body, toast])

  // Blue Mango tracker + photo reports (2026-10-18, Helena): the tracker workbook per supplier (one sheet per retailer,
  // every month since Nov 25, soh sheets) replaces the old this-month / last-month coverage files; the photo report per
  // retailer = every Georep / app visit with a photo, a sheet per week, newest first. services/fieldCoverage.js.
  const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
  const titleCase = (s) => String(s || "").toLowerCase().replace(/(^|[s_-])([a-z])/g, (m, a, b) => a + b.toUpperCase())
  const downloadXlsx = useCallback(async (path, payload, fileName) => {
    try {
      setExporting(true)
      const res = await fetch(`${API}/field/${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body(payload)) })
      const ct = res.headers.get("content-type") || ""
      if (!res.ok || !ct.includes("spreadsheetml")) {
        let message = `HTTP ${res.status}`
        try { message = (await res.json()).message || message } catch (e) { /* not json */ }
        throw new Error(message)
      }
      const blob = await res.blob()
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = fileName
      document.body.appendChild(link); link.click(); document.body.removeChild(link)
      window.URL.revokeObjectURL(link.href)
      toast({ title: "Downloaded", description: fileName, status: "success", duration: 4000, isClosable: true })
    } catch (err) {
      toast({ title: "Download failed", description: err.message, status: "error", duration: 6000, isClosable: true })
    } finally { setExporting(false) }
  }, [body, toast])
  const now = new Date()
  const monYY = `${MON[now.getMonth()]}${String(now.getFullYear()).slice(2)}`
  const coverageItems = routing ? [
    ...["ADRIATIC", "ALETTES", "CARING CANDY"].map((code) => ({ label: `Tracker · ${code}`, onClick: () => downloadXlsx("coverage-xlsx", { supplier: code }, `${titleCase(code).replace(/s+/g, "_")}_Tracker_${monYY}.xlsx`), icon: FaFileExcel })),
    ...[["DIS", "Dischem", "Dis-Chem"], ["CHK", "Checkers", "Checkers + MediRite"], ["CLI", "Clicks", "Clicks"]].map(([ret, file, label]) => ({ label: `Photo report · ${label}`, onClick: () => downloadXlsx("photo-report-xlsx", { retailer: ret }, `${file}_photo report.xlsx`), icon: FaFileExcel })),
  ] : []

  // KPI strip: the selected rep's totals (the tracker's total row), or every rep's when none.
  const kpiMeasures = useMemo(() => {
    if (tracker && tracker.rep === rep && tracker.total) return tracker.total
    return summary?.total || null
  }, [tracker, summary, rep])

  const period = options?.period || summary?.period || null
  const rt = routing?.total
  const strike = rt && rt.scheduled ? {
    pct: Math.round(((rt.visited + rt.late) / rt.scheduled) * 100),
    sub: `${(rt.visited + rt.late).toLocaleString("en-ZA")} of ${rt.scheduled.toLocaleString("en-ZA")} visits · ${(rt.missed || 0).toLocaleString("en-ZA")} missed`,
  } : null
  const reps = options?.reps || []
  const suppliers = options?.suppliers || []
  const costMissing = summary?.costMissing?.length ? summary.costMissing : options?.costMissing || []

  if (optionsError) {
    return (
      <Panel>
        <Text fontSize={type.size.body} fontWeight={type.weight.semibold} color={neutral[800]} mb={1}>
          {optionsError.code === "REP_NOT_SET_UP" ? "Rep page not set up" : "Rep page unavailable"}
        </Text>
        <Text fontSize={type.size.small} color={neutral[500]}>{optionsError.message}</Text>
      </Panel>
    )
  }

  const selectStyle = { size: "sm", bg: neutral[0], borderColor: neutral[300], fontSize: type.size.small, w: "auto", minW: "190px" }

  return (
    <Box>
      <PageToolbar
        description={`${options?.org?.org_name ? options.org.org_name + " · " : ""}${RET_LABEL[retailer]} · sales at cost per rep${retailer === "ALL" ? " (Dis-Chem file cost; others units × article cost)" : retailer !== "DIS" ? " (units × article cost)" : ""}${strike && period?.latestDay ? ` · data to ${period.latestDay}` : ""}`}
        filters={
          <>
            <Select {...selectStyle} minW="140px" value={retailer} onChange={(e) => setRetailer(e.target.value)} aria-label="Retailer">
              {(options?.retailers?.length ? options.retailers : Object.keys(RET_LABEL).map((k) => ({ value: k, text: RET_LABEL[k] }))).map((r) => (
                <option key={r.value} value={r.value}>{r.text}</option>
              ))}
            </Select>
            {/* no rep dropdown (Helena 2026-10-16: "we have all the reps in the first block"): click a rep's line in the
                All reps table to pick the rep, click it again for all reps */}
            <Select {...selectStyle} value={supplier} onChange={(e) => setSupplier(e.target.value)} isDisabled={!options} aria-label="Supplier">
              <option value={ALL}>Combined · all suppliers</option>
              {suppliers.map((s) => (
                <option key={s.value} value={s.value}>{s.text && s.text.trim().toUpperCase() !== String(s.value).toUpperCase() ? s.text : s.value}</option>
              ))}
            </Select>
          </>
        }
        actions={
          <ExportMenu
            isLoading={exporting}
            width="300px"
            items={[
              ...(rep === ALL
                ? [{ label: "Combined · summary, all stores + one sheet per rep", onClick: () => download(ALL), icon: FaFileExcel }]
                : [
                    { label: `Rep Sales · ${rep || "rep"}`, onClick: () => download(rep), icon: FaFileExcel },
                    { label: "All reps · summary, all stores + one sheet per rep", onClick: () => download(ALL), icon: FaFileExcel },
                  ]),
              ...coverageItems,
            ]}
          />
        }
      />

      {costMissing.length > 0 && (
        <Alert status="warning" variant="left-accent" borderRadius="md" mb={4} fontSize={type.size.small}>
          <AlertIcon />
          <AlertDescription>
            Sales at cost has not been captured for {costMissing.map((m) => m.label).join(", ")} — those cells show blank until that month's file is imported again.
          </AlertDescription>
        </Alert>
      )}

      <RepKpis measures={kpiMeasures} period={period} rep={tracker && tracker.rep === rep && rep !== ALL ? rep : null} isLoading={!options || (summaryLoading && trackerLoading)} strike={strike} />

      <Section
        title="All reps"
        description="Every rep's stores combined · click a rep to open their tracker"
        icon={FaUserTie}
        accent={brand[500]}
        meta={period ? <Text fontSize={type.size.caption} color={neutral[400]}>Data to {period.latestDay}</Text> : null}
      >
        {/* Side by side only on very wide screens — the eight-column table needs the width;
            below that the chart sits under it. */}
        {chartTarget || storesTarget || hideStores ? (
          <RepSummary data={summary} isLoading={summaryLoading && !summary} selectedRep={rep} onSelectRep={pickRep} routing={routing} onRoute={onRoute} />
        ) : (
          <Box display="grid" gridTemplateColumns={{ base: "1fr", "2xl": "minmax(0, 3fr) minmax(0, 2fr)" }} gap={4} alignItems="start">
            <RepSummary data={summary} isLoading={summaryLoading && !summary} selectedRep={rep} onSelectRep={pickRep} />
            <RepChart summary={summary} pair={pair} onPairChange={setPair} selectedRep={rep} />
          </Box>
        )}
      </Section>

      {hideStores ? null : storesTarget ? createPortal(<RepTracker data={tracker} isLoading={trackerLoading && !tracker} />, storesTarget) : (
      <Section
        title="Rep Sales"
        description={rep === ALL ? "Combined · every store of every rep, blank where nothing sold" : rep ? `${rep} · every assigned store, blank where nothing sold` : "Pick a rep"}
        icon={FaStore}
        accent="#8A63D2"
      >
        <RepTracker data={tracker} isLoading={trackerLoading && !tracker} />
      </Section>
      )}
      {chartTarget && createPortal(<RepChart summary={summary} pair={pair} onPairChange={setPair} selectedRep={rep} />, chartTarget)}
    </Box>
  )
}

export default Rep
